import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { emitCatalogUpdated } from "@/lib/catalogSync";

const CATALOGS = [
  { key: "laboratorios", label: "Laboratorios" },
  { key: "marcas", label: "Marcas" },
  { key: "presentaciones", label: "Presentaciones" },
  { key: "formas_farmaceuticas", label: "Formas farmacéuticas" },
  { key: "bodegas", label: "Bodegas" },
  { key: "formas_pago", label: "Formas de pago" },
  { key: "tipos_cliente", label: "Tipos de cliente" },
  { key: "tipos_proveedor", label: "Tipos de proveedor" },
];

export default function Catalogs() {
  const [current, setCurrent] = useState("laboratorios");
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", descripcion: "", activo: true });

  const load = async () => {
    try {
      const { data } = await api.get(`/masters/catalog/${current}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [current]);

  const openCreate = () => { setEditing(null); setForm({ nombre: "", descripcion: "", activo: true }); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ nombre: it.nombre, descripcion: it.descripcion || "", activo: it.activo }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/masters/catalog/${current}/${editing.id}`, form);
      else await api.post(`/masters/catalog/${current}`, form);
      toast.success("Guardado"); setDialogOpen(false); load();
      emitCatalogUpdated(current, editing ? "update" : "create");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.nombre}"?`)) return;
    try { await api.delete(`/masters/catalog/${current}/${it.id}`); toast.success("Eliminado"); load(); emitCatalogUpdated(current, "delete"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const currentLabel = CATALOGS.find(c => c.key === current)?.label;

  return (
    <div className="space-y-6" data-testid="catalogs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Catálogos
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">Datos base utilizados en productos, clientes y proveedores.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={current} onValueChange={setCurrent}>
            <SelectTrigger className="w-[220px]" data-testid="catalog-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATALOGS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-catalog-btn">
            <Plus size={16} className="mr-2" /> Nuevo
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Nombre</th><th>Descripción</th><th>Estado</th><th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="font-medium">{it.nombre}</td>
                <td className="text-[#4a5c53]">{it.descripcion || "—"}</td>
                <td>{it.activo ? <Badge className="fefo-safe border">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-[#8ba397]">Sin registros en {currentLabel}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} · {currentLabel}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} data-testid="catalog-nombre-input" /></div>
            <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} /></div>
            <div className="flex items-center gap-2"><Checkbox checked={form.activo} onCheckedChange={(v) => setForm({...form, activo: !!v})} /> <Label>Activo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-catalog-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
